/**
 * Contract Expiry Alert section (CR-7231).
 *
 * How many days before a contract's end date the customer is warned. The
 * calendar's Contract Renewal reminder falls this many days ahead, and the
 * daily expiry notification counts down inside the same window. A contract's
 * own reminder days (Supplier Profile contracts) take precedence. Mirrors the
 * Quality tab's Approval Expiry Alert card, as the ticket asked.
 */

import { useState, useEffect } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { AlertCircle, CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_DAYS = 30;
const MIN_DAYS = 1;
const MAX_DAYS = 365;

export function ContractExpiryAlertSection() {
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [days, setDays] = useState<number>(DEFAULT_DAYS);
  // Held as a string so the field can be cleared while typing; validated on
  // blur / Enter.
  const [daysInput, setDaysInput] = useState<string>(String(DEFAULT_DAYS));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const value = Number(data.contract_expiry_alert_days ?? DEFAULT_DAYS);
        const safe = Number.isInteger(value) && value >= MIN_DAYS && value <= MAX_DAYS
          ? value : DEFAULT_DAYS;
        setDays(safe);
        setDaysInput(String(safe));
      } catch (err: unknown) {
        console.error('Error loading Contract Expiry Alert setting:', err);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const commitDays = async () => {
    const trimmed = daysInput.trim();
    const parsed = /^\d+$/.test(trimmed) ? Number.parseInt(trimmed, 10) : NaN;
    if (!Number.isInteger(parsed) || parsed < MIN_DAYS || parsed > MAX_DAYS) {
      toast.error(`Enter a whole number of days between ${MIN_DAYS} and ${MAX_DAYS}`);
      setDaysInput(String(days));
      return;
    }
    if (parsed === days) {
      setDaysInput(String(parsed));
      return;
    }
    const previous = days;
    setDays(parsed);
    setDaysInput(String(parsed));
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ contract_expiry_alert_days: parsed });
      toast.success(`Contract Expiry Alert set to ${parsed} day${parsed === 1 ? '' : 's'} prior`);
    } catch (err: unknown) {
      console.error('Error saving Contract Expiry Alert setting:', err);
      toast.error('Failed to save Contract Expiry Alert');
      setDays(previous);
      setDaysInput(String(previous));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Contract Expiry Alert
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Warn your team ahead of a contract&apos;s end date, so there is time to renew or renegotiate it.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : loadFailed ? (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            Couldn&apos;t load this setting. Reload the page to try again.
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="contract-expiry-alert-days" className="text-sm text-muted-foreground">
              Contract Expiry Alert
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="contract-expiry-alert-days"
                type="number"
                inputMode="numeric"
                min={MIN_DAYS}
                max={MAX_DAYS}
                step={1}
                className="max-w-[150px]"
                value={daysInput}
                onChange={(e) => setDaysInput(e.target.value)}
                onBlur={commitDays}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                disabled={saving}
                aria-describedby="contract-expiry-alert-days-help"
              />
              <span className="text-sm text-muted-foreground">Days Prior</span>
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </div>
            <p id="contract-expiry-alert-days-help" className="text-xs text-muted-foreground">
              The calendar&apos;s Contract Renewal reminder falls this many days before a contract
              expires, and the contract owner (or your purchasing team, when the contract has no
              owner) is notified as the date approaches. A contract&apos;s own reminder days, set on
              the supplier profile, take precedence. Whole number between {MIN_DAYS} and {MAX_DAYS};
              default {DEFAULT_DAYS}.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
